import { ApolloClient, InMemoryCache, split } from "@apollo/client";
import { HttpLink } from "@apollo/client/link/http";
import { SetContextLink } from "@apollo/client/link/context";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { createClient } from "graphql-ws";
import { getEnvConfig } from "../config/config";
import { oktaAuth } from "../okta/okta";

// Routes subscriptions over a websocket and everything else over http.
function buildSplitLink(httpLink: HttpLink, websocketEndpoint: string) {
  // Create a WebSocket link using graphql-ws:
  const wsLink = new GraphQLWsLink(
    createClient({
      url: websocketEndpoint,
      connectionParams: async () => {
        const token = await oktaAuth.getAccessToken();
        return {
          authorization: token ? `Bearer ${token}` : "",
        };
      },
    })
  );

  // using the ability to split links, you can send data to each link
  // depending on what kind of operation is being sent
  return split(
    // split based on operation type
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === "OperationDefinition" &&
        definition.operation === "subscription"
      );
    },
    wsLink,
    httpLink
  );
}

export function createApolloClient() {
  const { httpEndpoint, websocketEndpoint, useSubscriptions } =
    getEnvConfig().GraphQL;

  // Create an http link:
  const httpLink = new HttpLink({
    uri: httpEndpoint,
  });

  // With subscriptions off there is nothing to route over a socket, so build the
  // link from http alone rather than creating a client that never connects.
  const link = useSubscriptions
    ? buildSplitLink(httpLink, websocketEndpoint)
    : httpLink;

  const authLink = new SetContextLink(async ({ headers }) => {
    // get the authentication token from Okta
    const token = await oktaAuth.getAccessToken();

    // return the headers to the context so httpLink can read them
    return {
      headers: {
        ...(headers as Record<string, string> | undefined),
        authorization: token ? `Bearer ${token}` : "",
      },
    };
  });

  return new ApolloClient({
    link: authLink.concat(link),
    cache: new InMemoryCache(),
  });
}
